import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { Add, Delete } from '@mui/icons-material'
import { com, kotlin, Nullable } from '@eidu/form'
import EntityTypeRepository from '../../io/EntityTypeRepository'
import FieldData, {
  ExistingFieldData,
  fieldDataFromExistingField,
  fieldDataFromNewField,
} from '../../domain/entity/FieldData'
import FieldInput from '../../components/input/FieldInput'
import LabelInput from '../../components/input/LabelInput'
import KindInput from '../../components/input/KindInput'
import { createEntityLabel, labelLineToString, stringToLabelLine } from '../../components/misc/LabelUtil'
import LoadingOverlay from '../../components/feedback/LoadingOverlay'
import { useSnackBar } from '../../components/feedback/SnackBarProvider'
import {
  prepareFieldNameUpdates,
  prepareFieldsToCreate,
  prepareFieldsToDelete,
} from '../../util/entityType/updateHelpers'
import ConfirmationDialog from '../../components/layout/ConfirmationDialog'
import EntityTypeId = com.eidu.sharedlib.entity.type.EntityTypeId
import EntityType = com.eidu.sharedlib.entity.type.EntityType
import EntityTypeKind = com.eidu.sharedlib.entity.type.EntityTypeKind
import Field = com.eidu.sharedlib.entity.field.Field
import FieldType = com.eidu.sharedlib.entity.field.FieldType
import fieldIdFromString = com.eidu.sharedlib.entity.field.fieldIdFromString
import EntityTypeValidator = com.eidu.sharedlib.entity.validation.EntityTypeValidator
import TypeValidationError = com.eidu.sharedlib.entity.validation.TypeValidationError
import LabelTypeValidationError = com.eidu.sharedlib.entity.validation.LabelTypeValidationError
import KtList = kotlin.collections.KtList

type ManageEntityTypePageProps = {
  entityTypeId: EntityTypeId | undefined
  entityTypeRepository: EntityTypeRepository
}

type ValidationState = {
  nameErrors?: readonly TypeValidationError[]
  fieldErrors?: Nullable<readonly TypeValidationError[]>[]
  primaryLabelErrors?: readonly TypeValidationError[]
  secondaryLabelErrors?: readonly TypeValidationError[]
  formError?: string
}

const createNewFieldData = (existingFields: readonly FieldData[]): FieldData => {
  const existingNames = new Set(existingFields.map((field) => field.field.name))
  let counter = existingFields.length + 1
  let fieldName = `Field ${counter}`
  while (existingNames.has(fieldName)) {
    counter += 1
    fieldName = `Field ${counter}`
  }
  const newField = Field.createSimple(fieldIdFromString(crypto.randomUUID()), fieldName, FieldType.Text, false, null)
  return fieldDataFromNewField(newField, existingFields.length)
}

const validateLabelLine = (
  value: string,
  fields: readonly FieldData[],
  allowReferences: boolean
): readonly TypeValidationError[] | undefined => {
  if (value.trim() === '') return undefined
  const labelLine = stringToLabelLine(value, fields)
  if (!labelLine) return [new LabelTypeValidationError('Label contains invalid field references')]
  return EntityTypeValidator.validateLabelLineWithFieldsList(
    labelLine,
    KtList.fromJsArray(fields.map((it) => it.field)),
    allowReferences
  )?.asJsReadonlyArrayView()
}

const validateState = (
  entityTypeName: string,
  fields: readonly FieldData[],
  primaryLabel: string,
  secondaryLabel: string
): ValidationState => {
  const allFields = fields.map((field) => field.field)
  const nameErrors = EntityTypeValidator.validateNameString(entityTypeName)?.asJsReadonlyArrayView()
  const fieldValidationResults = fields.map(
    (field) =>
      EntityTypeValidator.validateField(field.field, KtList.fromJsArray(allFields))?.asJsReadonlyArrayView() ?? null
  ) as Nullable<readonly TypeValidationError[]>[]

  const primaryLabelErrors = validateLabelLine(primaryLabel, fields, false)
  const secondaryLabelErrors = validateLabelLine(secondaryLabel, fields, true)

  let formError: string | undefined
  if (fields.length === 0) formError = 'At least one field is required.'
  else if (primaryLabel.trim() === '') formError = 'Primary label is required.'

  return { nameErrors, fieldErrors: fieldValidationResults, primaryLabelErrors, secondaryLabelErrors, formError }
}

const ManageEntityTypePage = ({ entityTypeId, entityTypeRepository }: ManageEntityTypePageProps) => {
  const navigate = useNavigate()
  const { showSnackBar } = useSnackBar()

  const entityTypes = entityTypeRepository.useAll()
  const repositoryError = entityTypeRepository.useError()?.let((it) => String(it))
  const repositoryLoading = entityTypeRepository.useLoading()

  const entityType = entityTypeId ? entityTypeRepository.useItem(entityTypeId) : undefined

  const [entityTypeName, setEntityTypeName] = useState('')
  const [primaryLabel, setPrimaryLabel] = useState('')
  const [secondaryLabel, setSecondaryLabel] = useState('')
  const [kind, setKind] = useState<EntityTypeKind>(EntityTypeKind.Generic)
  const [fields, setFields] = useState<readonly FieldData[]>([])
  const [originalFields, setOriginalFields] = useState<readonly FieldData[]>([])
  const [validation, setValidation] = useState<ValidationState>({})
  const [submitting, setSubmitting] = useState(false)
  const [fieldPendingDeletionIndex, setFieldPendingDeletionIndex] = useState<number | null>(null)

  useEffect(() => {
    if (entityTypeId) {
      if (entityType === undefined) return
      if (entityType === null) {
        setFields([])
        setOriginalFields([])
        setEntityTypeName('')
        return
      }

      const existingFieldData = entityType.fields
        .asJsReadonlyArrayView()
        .map((field) => fieldDataFromExistingField(field, undefined)) as readonly ExistingFieldData[]

      setFields(existingFieldData)
      setOriginalFields(existingFieldData)
      setEntityTypeName(entityType.name)
      setKind(entityType.kind)
      const fieldList = entityType.fields.asJsReadonlyArrayView()
      setPrimaryLabel(labelLineToString(entityType.label.primary, fieldList))
      setSecondaryLabel(entityType.label.secondary?.let((line) => labelLineToString(line, fieldList)) ?? '')
      setValidation({})
    } else {
      setEntityTypeName('')
      setFields([])
      setOriginalFields([])
      setPrimaryLabel('')
      setSecondaryLabel('')
      setKind(EntityTypeKind.Generic)
      setValidation({})
    }
    setFieldPendingDeletionIndex(null)
  }, [entityTypeId, entityType])

  useEffect(() => {
    if (!entityTypeId && fields.length > 0 && primaryLabel.trim() === '') {
      setPrimaryLabel(`{${fields[0].field.name}}`)
    }
  }, [fields, entityTypeId, primaryLabel])

  const availableEntityTypes = useMemo(
    () =>
      entityTypes
        ? Array.from(entityTypes.values()).filter((type): type is EntityType => type !== null && type !== undefined)
        : [],
    [entityTypes]
  )

  const updateFieldAt = async (index: number, update: (field: FieldData) => FieldData) => {
    const updated = fields.map((field, currentIndex) => (currentIndex === index ? update(field) : field))
    setFields(updated)
  }

  const handleFieldTypeChange = async (index: number, typeName: string) => {
    if (fields[index]?.existing) return
    await updateFieldAt(index, (field) => ({
      ...field,
      field: field.field.withType(FieldType.valueOf(typeName)),
    }))
  }

  const handleFieldRequiredChange = async (index: number) => {
    if (fields[index]?.existing) return
    await updateFieldAt(index, (field) => ({
      ...field,
      field: field.field.withRequired(!field.field.required),
    }))
  }

  const handleFieldReferenceChange = async (index: number, referenceTypes: readonly EntityTypeId[]) => {
    if (fields[index]?.existing) return
    await updateFieldAt(index, (field) => ({
      ...field,
      field: field.field.withValidReferenceTypes(
        referenceTypes.length > 0 ? kotlin.collections.KtSet.fromJsSet(new Set(referenceTypes)) : undefined
      ),
    }))
  }

  const handleFieldNameChange = async (index: number, newName: string) => {
    await updateFieldAt(index, (field) => ({
      ...field,
      field: field.field.withName(newName),
    }))
  }

  const addField = () => {
    setFields((current) => [...current, createNewFieldData(current)])
  }

  const removeField = (index: number) => {
    setFields((current) => current.filter((_, currentIndex) => currentIndex !== index))
  }

  const handleFieldRemoval = (index: number) => {
    const field = fields[index]
    if (!field) return
    if (field.existing) {
      setFieldPendingDeletionIndex(index)
      return
    }
    removeField(index)
  }

  const closeFieldDeletionDialog = () => setFieldPendingDeletionIndex(null)

  const confirmFieldDeletion = () => {
    if (fieldPendingDeletionIndex === null) return
    removeField(fieldPendingDeletionIndex)
    setFieldPendingDeletionIndex(null)
  }

  const deleteType = async () => {
    if (!entityTypeId) return
    const type = await entityTypeRepository.get(entityTypeId)
    if (type === null) {
      showSnackBar('Type not found', 'error')
      return
    }
    setSubmitting(true)
    try {
      await entityTypeRepository.removeEntityType(entityTypeId)
      showSnackBar(`Deleted type ${type.name}`, 'success')
      navigate('/types')
    } catch (_error) {
      showSnackBar('Failed to delete type', 'error')
    } finally {
      setSubmitting(false)
    }
  }

  const handleSubmit = async () => {
    const currentValidation = validateState(entityTypeName, fields, primaryLabel, secondaryLabel)
    setValidation(currentValidation)
    if (
      currentValidation.formError ||
      currentValidation.nameErrors ||
      currentValidation.primaryLabelErrors ||
      currentValidation.secondaryLabelErrors ||
      currentValidation.fieldErrors?.some((it) => it && it.length > 0)
    )
      return

    const label = createEntityLabel({ primaryLabel, secondaryLabel, fields })
    const fieldsToCreate = prepareFieldsToCreate(fields)
    const fieldsToUpdate = prepareFieldNameUpdates(fields, originalFields)
    const fieldsToDelete = prepareFieldsToDelete(fields, originalFields)

    setSubmitting(true)
    try {
      if (entityTypeId) {
        await entityTypeRepository.modifyEntityType(
          entityTypeId,
          entityTypeName !== entityType?.name ? entityTypeName : undefined,
          label,
          fieldsToCreate,
          fieldsToUpdate,
          fieldsToDelete
        )
        showSnackBar('Type saved', 'success')
      } else {
        if (!label) {
          showSnackBar('Primary label is required', 'error')
          setSubmitting(false)
          return
        }
        await entityTypeRepository.createEntityType(entityTypeName, fieldsToCreate, label, kind)
        showSnackBar('Type created', 'success')
      }
      navigate(-1)
    } catch (_error) {
      showSnackBar('Failed to save type', 'error')
    } finally {
      setSubmitting(false)
    }
  }

  const isLoading = repositoryLoading || submitting || (entityTypeId !== undefined && entityType === undefined)
  const pendingDeletionFieldName =
    fieldPendingDeletionIndex !== null ? fields[fieldPendingDeletionIndex]?.field.name ?? 'this field' : 'this field'

  return (
    <>
      <Stack padding={3} spacing={3}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h4">{entityTypeId ? 'Edit Type' : 'Create Type'}</Typography>
          {entityTypeId && (
            <ConfirmationDialog
              title="Are you sure you want to delete this type? This action cannot be undone."
              content={
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Delete fontSize="small" />
                  <Typography>Delete type</Typography>
                </Stack>
              }
              onConfirm={deleteType}
            />
          )}
        </Stack>

        <Stack spacing={2}>
          <Typography variant="h6">Name</Typography>
          <TextField
            required
            label="Type name"
            value={entityTypeName}
            onChange={(event) => setEntityTypeName(event.target.value)}
            error={!!validation.nameErrors && validation.nameErrors.length > 0}
            helperText={validation.nameErrors?.map((error) => error.message).join(' • ')}
          />
        </Stack>

        <Stack spacing={2}>
          <Typography variant="h6">Kind</Typography>
          <KindInput kind={kind} setKind={setKind} editable={!entityTypeId} />
        </Stack>

        <Divider />

        <Stack spacing={3}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="h5">Fields</Typography>
            <Button startIcon={<Add />} variant="outlined" onClick={addField}>
              Add field
            </Button>
          </Stack>

          {fields.length === 0 && (
            <Typography color="text.secondary">Add at least one field to define the type.</Typography>
          )}

          {fields.map((fieldData, index) => (
            <Box
              key={fieldData.field.id?.toString() ?? `${fieldData.field.name}-${index}`}
              sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, padding: 2 }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                <Typography variant="subtitle1">{fieldData.field.name || `Field ${index + 1}`}</Typography>
                <IconButton color="error" onClick={() => handleFieldRemoval(index)} size="small" disabled={submitting}>
                  <Delete fontSize="small" />
                </IconButton>
              </Stack>
              <Box mt={2}>
                <FieldInput
                  availableEntityTypes={availableEntityTypes}
                  disabled={submitting}
                  editable={!fieldData.existing}
                  fieldType={fieldData.field.type}
                  handleFieldTypeChange={(event) => handleFieldTypeChange(index, event.target.value)}
                  handleRequiredChange={() => handleFieldRequiredChange(index)}
                  handleReferenceTypesChange={(types: readonly EntityTypeId[]) =>
                    handleFieldReferenceChange(index, types)
                  }
                  handleFieldNameChange={(newName: string) => handleFieldNameChange(index, newName)}
                  idPrefix={`manage-entity-type-field-${index}`}
                  name={fieldData.field.name}
                  referenceTypes={
                    fieldData.field.validReferenceTypes?.asJsReadonlySetView()?.let((set) => Array.from(set)) || []
                  }
                  referenceTypesError={
                    fieldData.field.type === FieldType.Reference
                      ? !fieldData.field.validReferenceTypes?.asJsReadonlySetView()?.let((set) => set.size > 0)
                      : false
                  }
                  required={fieldData.field.required}
                  updating={false}
                  errors={validation.fieldErrors?.[index] ?? undefined}
                  editableName
                />
              </Box>
            </Box>
          ))}
        </Stack>

        <Divider />

        <LabelInput
          fields={fields}
          primaryLabel={primaryLabel}
          setPrimaryLabel={setPrimaryLabel}
          primaryLabelErrors={validation.primaryLabelErrors}
          secondaryLabel={secondaryLabel}
          setSecondaryLabel={setSecondaryLabel}
          secondaryLabelErrors={validation.secondaryLabelErrors}
          primaryLabelEditable
          secondaryLabelEditable
        />

        <Collapse in={!!validation.formError}>
          <Alert severity="error">{validation.formError}</Alert>
        </Collapse>

        <Collapse in={!!repositoryError}>
          <Alert severity="error">{repositoryError}</Alert>
        </Collapse>

        <Stack direction="row" justifyContent="flex-end" spacing={2}>
          <Button onClick={() => navigate(-1)} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
            Save
          </Button>
        </Stack>
      </Stack>
      <LoadingOverlay isOpen={isLoading} />
      <Dialog open={fieldPendingDeletionIndex !== null} onClose={closeFieldDeletionDialog}>
        <DialogTitle>Confirm your action</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {`Deleting the field "${pendingDeletionFieldName}" will remove it from this type. This action cannot be undone.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeFieldDeletionDialog}>Cancel</Button>
          <Button color="error" onClick={confirmFieldDeletion}>
            Delete field
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default ManageEntityTypePage
